// Patient before-and-after grid for /results.
//
// Kept in its own module so the route can render it synchronously on the
// server (where the whole grid, with every caption, alt text and credit, is
// baked into the delivered HTML) while the browser only ever downloads the
// story catalog when someone reaches /results through a client-side link.
import { Link } from "@tanstack/react-router";
import { isPhraseLabel } from "@/lib/story-titles";
import { stories, storySlug, storyCredit, type Story } from "@/lib/results-stories";

export function StoryCard({ s, i }: { s: Story; i: number }) {
  return (
    <article id={storySlug(s, i)} className="rounded-2xl bg-card border border-border overflow-hidden shadow-sm">
      {s.single ? (
        <figure className="m-0">
          <img
            src={s.before.url}
            srcSet={s.before.srcSet}
            sizes={s.before.sizes}
            width={s.before.width}
            height={s.before.height}
            alt={`${s.label} — weight-loss progress after bariatric surgery at Virginia Bariatric Surgery`}
            loading={i === 0 ? "eager" : "lazy"}
            {...(i === 0 ? { fetchPriority: "high" as const } : { decoding: "async" as const })}
            className={`${s.aspectClass ?? ""} w-full ${s.aspectClass ? "object-cover" : "h-auto"} block`}
          />
          <figcaption className="px-4 pt-2 text-sm text-muted-foreground">
            {s.caption}
            <small className="mt-1 block text-xs text-muted-foreground/80">
              {storyCredit(s).creditLine}
            </small>
          </figcaption>
        </figure>
      ) : (
        <figure className="m-0">
          <div className="grid grid-cols-2">
            <div className="m-0">
              <img
                src={s.before.url}
                srcSet={s.before.srcSet}
                sizes={s.before.sizes}
                width={s.before.width}
                height={s.before.height}
                alt={`${s.label} before weight-loss surgery with Dr. Matthew Fitzer`}
                loading={i === 0 ? "eager" : "lazy"}
                {...(i === 0 ? { fetchPriority: "high" as const } : { decoding: "async" as const })}
                style={{ objectPosition: s.beforePosition ?? "center top" }}
                className={`${s.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
              />
            </div>
            <div className="m-0">
              <img
                src={s.after.url}
                srcSet={s.after.srcSet}
                sizes={s.after.sizes}
                width={s.after.width}
                height={s.after.height}
                alt={`${s.label} after weight-loss surgery with Dr. Matthew Fitzer`}
                loading={i === 0 ? "eager" : "lazy"}
                {...(i === 0 ? { fetchPriority: "high" as const } : { decoding: "async" as const })}
                style={{ objectPosition: s.afterPosition ?? "center top" }}
                className={`${s.aspectClass ?? "aspect-[4/3]"} w-full object-cover`}
              />
            </div>
          </div>
          <figcaption className="px-4 pt-2 text-sm text-muted-foreground">
            {s.caption}
            <small className="mt-1 block text-xs text-muted-foreground/80">
              {storyCredit(s).creditLine}
            </small>
          </figcaption>
        </figure>
      )}
      <div className="px-4 py-3 border-t border-border">
        <p className="font-serif text-2xl">{s.name}</p>
        {s.tagline && (
          <p className="mt-0.5 text-base text-muted-foreground">{s.tagline}</p>
        )}
        {s.quote && (
          <p className="mt-1 text-foreground/80 leading-[1.6]">{s.quote}</p>
        )}
        <Link
          to="/results/story/$slug"
          params={{ slug: storySlug(s, i) }}
          className="mt-2 inline-block text-sm font-medium text-brand-logo hover:underline"
        >
          {isPhraseLabel(s.label)
            ? "View & share this story"
            : `View & share ${s.label}’s story`}
          {" "}&rarr;
        </Link>
      </div>
    </article>
  );
}

export default function ResultsStoryGrid() {
  return (
    <>
      {stories.map((s, i) => (
        <StoryCard key={`${s.name}-${i}`} s={s} i={i} />
      ))}
    </>
  );
}
